import { Box } from "@outschool/backpack";
import {
  CategoryPage as CategoryPageType,
  ContentfulMagnetPage,
  SavedSearchFilters,
  SearchFilters
} from "@outschool/gql-backend-generated";
import { SearchUseCase } from "@outschool/gql-frontend-generated";
import { TFunction, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { getEsaState, getEsaWaitPeriod, useSession } from "@outschool/ui-auth";
import { ErrorMessage } from "@outschool/ui-components-shared";
import {
  CategoryPageContentfulBanner,
  CategoryPageVisitHistory,
  EnglishOnly,
  SimilarArticlesMagnetPageCarousel,
  TutorVideoCarousel,
  useTutoringCLPFiltersExperiment,
  useTutoringCLPFiltersMobileExperiment
} from "@outschool/ui-components-website";
import {
  Container,
  Loading,
  Space
} from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import { assign, isNil, omitBy } from "lodash";
import _omit from "lodash/omit";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import * as Env from "../../../../shared/Env";
import { extractAges } from "../../../../shared/User";
import { useCategoryPageQuery } from "../../../hooks/useCategoryPageQuery";
import {
  useCuratedCategoryPageQuery,
  useCuratedCategoryPageRelatedCarouselsQuery
} from "../../../hooks/useCuratedCategoryPageQuery";
import useTrackSearchFiltersApplied from "../../../hooks/useTrackSearchFiltersApplied";
import IterableEmailCaptureContentfulBox from "../../IterableEmailCaptureContentful";
import TutorMatchFilters from "../../onboarding/TutorMatchFilters";
import { DefaultErrorBoundaryMessage } from "../../SimpleErrorBoundary";
import CategoryBreadcrumbs from "../CategoryBreadcrumbs";
import CategoryPills from "../CategoryPills";
import { CategoryListingsFilters } from "../filters/CategoryListingsFilters";
import { HeroFiltersButtonMobile } from "../filters/HeroFiltersButtonMobile";
import { useListingsData } from "../hooks/useListingsData";
import RelatedCategories from "../RelatedCategories";
import CuratedBelowTheFoldSection from "./CuratedBelowTheFoldSection";
import CuratedCategoryPageActivityGrid from "./CuratedCategoryActivityGrid";
import CuratedCategoryHeroSection from "./CuratedCategoryHeroSection";
import CuratedCategoryJumpPillsCarousel from "./CuratedCategoryJumpPillsCarousel";
import CuratedReviews from "./CuratedReviews";

function CuratedPageBody(
  {
    categorySlug,
    curatedCategoryPageQueryResult,
    taxonomyQueryResult,
    trackSearchFiltersApplied,
    preview,
    searchUid
  }: {
    categorySlug: string;
    curatedCategoryPageQueryResult: ReturnType<
      typeof useCuratedCategoryPageQuery
    >;
    taxonomyQueryResult: ReturnType<typeof useCategoryPageQuery>;
    trackSearchFiltersApplied: ReturnType<
      typeof useTrackSearchFiltersApplied
    >["trackSearchFiltersApplied"];
    preview: boolean;
    searchUid: string;
  },
  ref: React.Ref<HTMLDivElement>
) {
  const location = useLocation();
  const { t } = useTranslation(
    "client\\components\\categories\\CuratedCategoryPageBody"
  );

  const { data: contentfulCategoryData } = curatedCategoryPageQueryResult;
  const curatedCategoryPage =
    contentfulCategoryData?.contentfulCategoryPageCollection?.items[0];

  const magnetPagesCollection =
    curatedCategoryPage &&
    curatedCategoryPage?.magnetPagesCollection?.items?.length
      ? (curatedCategoryPage?.magnetPagesCollection
          ?.items as ContentfulMagnetPage[])
      : [];

  const { data: taxonomyData } = taxonomyQueryResult;
  const category = taxonomyData?.categoryPage;

  const {
    data: relatedCarouselsData,
    loading: relatedCarouselsLoading,
    error: relatedCarouselsError
  } = useCuratedCategoryPageRelatedCarouselsQuery({
    categorySlug,
    preview: !!preview
  });

  const predefinedCategoryFilters = usePredefinedFilters({
    // @ts-ignore TS(2322): Type 'CategoryPage | undefined' is not assignable ... Remove this comment to see the full error message
    category,
    curatedCategoryPage
  });

  const promotedRankingsEnabled = Env.FEATURE_FLAGS.PROMOTED_SEARCH_LOGGING;

  const {
    error: listingError,
    listings,
    loading: loadingListings,
    searchFilters,
    updateSearchFilters,
    exactTotal,
    fetchMore
  } = useListingsData({
    categorySlug,
    predefinedCategoryFilters,
    trackSearchFiltersApplied,
    skip: curatedCategoryPage === undefined,
    pageLocation: "CURATED_CATEGORY",
    usePromotedRanking: promotedRankingsEnabled,
    useCase: SearchUseCase.CategoryContent
  });

  const carouselSearchFilters = useMemo(
    () =>
      _omit(searchFilters, [
        "q",
        "order",
        "adminTags",
        "fundingPrograms",
        "multiTermQuery"
      ]),
    [searchFilters]
  );
  const [isTutoringCLP, setIsTutoringCLP] = useState(false);

  useEffect(() => {
    if (searchFilters.capacityMax === 1) {
      setIsTutoringCLP(true);
    }
    if (categorySlug.includes("tutor")) {
      setIsTutoringCLP(true);
    }
  }, [
    setIsTutoringCLP,
    searchFilters.capacityMax,
    searchFilters.format,
    categorySlug
  ]);

  const isTutoringCLPFilters = useTutoringCLPFiltersExperiment();
  const isTutoringCLPFiltersMobile = useTutoringCLPFiltersMobileExperiment();
  const useTutorMatchVariant =
    isTutoringCLP && (isTutoringCLPFilters || isTutoringCLPFiltersMobile);
  const isMobile = useIsMobile();

  const [showPersonalizeButton, setShowPeronalizeButton] = useState(true);
  useEffect(() => {
    if (!curatedCategoryPage) {
      return;
    }
    CategoryPageVisitHistory.pushToTop(categorySlug);
  }, [categorySlug, curatedCategoryPage]);

  if (
    !category ||
    !contentfulCategoryData ||
    !curatedCategoryPage ||
    !taxonomyData
  ) {
    return null;
  }

  if (!curatedCategoryPage) {
    console.log(
      `ERROR: Curated category page slug "${categorySlug}" not found in Contentful`
    );
    return <DefaultErrorBoundaryMessage />;
  }

  const filteredAncestors =
    category.ancestors?.flatMap(a => (!!a ? [a] : [])) ?? [];

  return (
    <>
      <Box
        ref={ref}
        sx={{
          overflow: "hidden"
        }}
      >
        {isMobile && showPersonalizeButton && useTutorMatchVariant && (
          <HeroFiltersButtonMobile
            category={category}
            updateSearchFilters={updateSearchFilters}
            searchFilters={searchFilters}
            setShowButton={setShowPeronalizeButton}
          />
        )}
        <CuratedCategoryHeroSection
          Breadcrumbs={
            <CategoryBreadcrumbs
              treatment={true}
              ancestors={filteredAncestors}
              currentCategoryName={category.nameTitleCased}
              categorySlug={category.slug}
              pathname={location.pathname}
            />
          }
          category={category}
          page={curatedCategoryPage}
          searchFilters={searchFilters}
          updateSearchFilters={updateSearchFilters}
          isTutoringCLP={useTutorMatchVariant}
        />
      </Box>
      {!useTutorMatchVariant && <Space y="medium" />}
      <CategoryPageContentfulBanner />
      {!useTutorMatchVariant && <Space y="medium" />}
      <Box>
        {useTutorMatchVariant ? (
          <TutorMatchFilters
            filters={searchFilters}
            categorySetFilters={updateSearchFilters}
            showTheme={false}
          />
        ) : (
          <CategoryListingsFilters
            updateListings={updateSearchFilters}
            resetSearchFilters={() => {
              // NOTE: Explicitly null out the age because it defaults to the
              // logged in user childern ages.
              updateSearchFilters(
                assign(predefinedCategoryFilters, { age: "" })
              );
            }}
            searchFilters={searchFilters}
            preSetFilters={predefinedCategoryFilters as SavedSearchFilters}
            isCuratedCategoryFilter={true}
          />
        )}
        <Space y={"1em"} />
        <Box>
          <CuratedCategoryJumpPillsCarousel
            categoryName={category.nameSentenceCased}
            data={relatedCarouselsData}
            page={curatedCategoryPage}
          />
          {!useTutorMatchVariant && <Space y={"1em"} />}

          <CuratedCategoryPageActivityGrid
            loading={loadingListings || exactTotal === null}
            error={listingError}
            searchFilters={searchFilters}
            loadedCategory={category}
            exactTotal={exactTotal}
            listings={listings}
            searchUid={searchUid}
            fetchMore={fetchMore}
            isTutoringCLP={isTutoringCLP}
          />

          {category.slug.includes("tutor") && !useTutorMatchVariant ? (
            <>
              <Space y={"large"} />
              <TutorVideoCarousel />
              <Space y={"large"} />
            </>
          ) : null}
          <Space y={"large"} />
          <IterableEmailCaptureContentfulBox categorySlug={category.slug} />
          <Space y={"large"} />
          {!useTutorMatchVariant && (
            <CuratedReviews
              category={category}
              filters={predefinedCategoryFilters as SearchFilters}
            />
          )}
          <Space y={"large"} />
        </Box>
        <RelatedCategories
          data={relatedCarouselsData}
          loading={relatedCarouselsLoading}
          carouselSearchFilters={carouselSearchFilters}
          isTutoringCLP={isTutoringCLP}
        />
        {relatedCarouselsLoading && !relatedCarouselsData && <Loading />}
        {relatedCarouselsError && (
          <ErrorMessage
            value={relatedCarouselsError}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
        )}
        <Space y={"xlarge"} />
      </Box>

      <Container>
        <CuratedBelowTheFoldSection
          title={curatedCategoryPage.belowTheFoldTitle}
          description={curatedCategoryPage.belowTheFoldDescription}
        />
      </Container>
      <EnglishOnly>
        <MagnetPageArticles collection={magnetPagesCollection} t={t} />
      </EnglishOnly>
      <Space y={"xlarge"} />
      <CategoryPills category={category} isTutoringCLP={isTutoringCLP} />
    </>
  );
}

const getFiltersOverride = ({
  category,
  response
}: {
  category?: ReturnType<
    typeof useCuratedCategoryPageQuery
    // @ts-ignore TS(2339): Property 'contentfulCategoryPageCollection' does n... Remove this comment to see the full error message
  >["data"]["contentfulCategoryPageCollection"]["items"]["0"];
  response?: ReturnType<typeof useCategoryPageQuery>["data"];
}) => {
  return !!category?.featuredClassesOverrideSlug &&
    !!response?.categoryPage?.searchFilters
    ? omitBy(response.categoryPage?.searchFilters, isNil)
    : category?.featuredClassesOverrideSlug
    ? {
        q: category?.featuredClassesOverrideSlug
      }
    : null;
};

const MagnetPageArticles: React.FC<{
  collection: ContentfulMagnetPage[];
  t: TFunction;
}> = memo(({ collection, t }) => {
  if (collection?.length === 0) {
    return null;
  }

  return (
    <SimilarArticlesMagnetPageCarousel
      title={t("Recommended articles")}
      articles={collection}
      leftAlign
      carouselSx={{ maxWidth: "100%", width: "100%" }}
    />
  );
});

function usePredefinedFilters({
  curatedCategoryPage,
  category
}: {
  curatedCategoryPage: ReturnType<
    typeof useCuratedCategoryPageQuery
    // @ts-ignore TS(2339): Property 'contentfulCategoryPageCollection' does n... Remove this comment to see the full error message
  >["data"]["contentfulCategoryPageCollection"]["items"]["0"];
  category: CategoryPageType;
}) {
  const { currentUser } = useSession();
  const esaState = getEsaState();
  const esaWaitPeriod = getEsaWaitPeriod();

  const { data: featuredClassesOverrideSlugData } = useCategoryPageQuery(
    curatedCategoryPage?.featuredClassesOverrideSlug,
    {
      skip: isNil(curatedCategoryPage?.featuredClassesOverrideSlug)
    }
  );

  return useMemo(() => {
    const filterOverrides = getFiltersOverride({
      category: curatedCategoryPage,
      response: featuredClassesOverrideSlugData
    });

    const filters = filterOverrides ?? {
      ...category?.searchFilters,
      standards: null,
      curriculums: null,
      order: "popular"
    };

    filters.fundingPrograms = esaState
      ? [...(filters.fundingPrograms || []), esaState]
      : [...(filters.fundingPrograms || [])];

    if (filters.age === null) {
      filters.age = extractAges(currentUser?.children).join(",");
    }

    const esaStartAfter = dayjs()
      .add(esaWaitPeriod, "days")
      .format("YYYY-MM-DD");
    if (!!esaState && !curatedCategoryPage?.startAfter) {
      filters.startAfter = esaStartAfter;
    }
    if (
      curatedCategoryPage?.startAfter &&
      dayjs(curatedCategoryPage?.startAfter) > dayjs(esaStartAfter)
    ) {
      filters.startAfter = curatedCategoryPage.startAfter;
    }

    return filters;
  }, [
    category,
    curatedCategoryPage,
    currentUser,
    featuredClassesOverrideSlugData,
    esaState,
    esaWaitPeriod
  ]);
}

export default React.forwardRef(CuratedPageBody);
